import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios";
import { useNotifications } from "@via/hooks/useNotifications";
import { type ErrorNotification } from "@via/utils/interfaces";
import {
  BillingInfoResponseBody,
  CheckSubscriptionHealthResponseBody,
  GetInvoicesQuery,
  GetInvoicesResponseBody,
  LicenseProductInfoResponseBody,
  SetCancelAtPeriodEndRequestBody,
  SetCancelAtPeriodEndResponseBody,
  SubscribeBillingRequestBody,
  SubscribeBillingResponseBody,
  UpdatePaymentMethodRequestBody,
  UpdatePaymentMethodResponseBody,
} from "common";

// --- API Methods ---

export const createSetupIntent = async (): Promise<{
  clientSecret?: string;
  isStripeDisabled?: boolean;
}> => {
  const res = await axios.post("/api/v1/billing/setupIntent");
  return res.data;
};

export const useCreateSetupIntent = (): UseMutationResult<
  { clientSecret?: string; isStripeDisabled?: boolean },
  ErrorNotification,
  void
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation<
    { clientSecret?: string; isStripeDisabled?: boolean },
    ErrorNotification,
    void
  >({
    mutationFn: createSetupIntent,
    onSuccess: data => {
      if (data.isStripeDisabled) {
        queryClient.invalidateQueries({
          queryKey: ["/api/auth/v1/user"],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/auth/v1/status"],
        });
      }
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "setup-intent-failed",
        message: "Failed to create Setup Intent",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const subscribe = async ({
  paymentMethodId,
}: SubscribeBillingRequestBody): Promise<SubscribeBillingResponseBody> => {
  const res = await axios.post("/api/v1/billing/subscribe", {
    paymentMethodId,
  });
  return res.data;
};

export const useSubscribe = (): UseMutationResult<
  SubscribeBillingResponseBody,
  ErrorNotification,
  SubscribeBillingRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation<
    SubscribeBillingResponseBody,
    ErrorNotification,
    SubscribeBillingRequestBody
  >({
    mutationFn: subscribe,
    onSuccess: (data: SubscribeBillingResponseBody): void => {
      setNotification({
        id: "subscription-success",
        message: "Subscription successful",
        details:
          data?.message || "Your subscription has been successfully created.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "subscription-failed",
        message: "Subscription failed",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchLicenseProductInfo = async (
  paymentMethodId: string
): Promise<LicenseProductInfoResponseBody> => {
  try {
    const baseUrl = "/api/v1/billing/license-product-info";
    const queryParams = new URLSearchParams({
      paymentMethodId,
    }).toString();
    const res = await axios.get(`${baseUrl}?${queryParams}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useLicenseProductInfo = (
  paymentMethodId: string
): UseQueryResult<LicenseProductInfoResponseBody, ErrorNotification> => {
  return useQuery<LicenseProductInfoResponseBody, ErrorNotification>({
    queryKey: ["/api/v1/billing/license-product-info", paymentMethodId],
    queryFn: async () => await fetchLicenseProductInfo(paymentMethodId),
    retry: false,
  });
};

export const fetchBillingInfo = async (): Promise<BillingInfoResponseBody> => {
  try {
    const res = await axios.get("/api/v1/billing/info");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useBillingInfo = (): UseQueryResult<
  BillingInfoResponseBody,
  ErrorNotification
> => {
  return useQuery<BillingInfoResponseBody, ErrorNotification>({
    queryKey: ["/api/v1/billing/info"],
    queryFn: fetchBillingInfo,
    retry: false,
  });
};

export const updateBillingInfo = async ({
  paymentMethodId,
}: UpdatePaymentMethodRequestBody): Promise<UpdatePaymentMethodResponseBody> => {
  const res = await axios.patch("/api/v1/billing/update-payment-method", {
    paymentMethodId,
  });
  return res.data;
};

export const useUpdateBillingInfo = (): UseMutationResult<
  UpdatePaymentMethodResponseBody,
  ErrorNotification,
  UpdatePaymentMethodRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation<
    UpdatePaymentMethodResponseBody,
    ErrorNotification,
    UpdatePaymentMethodRequestBody
  >({
    mutationFn: updateBillingInfo,
    onSuccess: (data: UpdatePaymentMethodResponseBody): void => {
      setNotification({
        id: "update-billing-success",
        message: "Billing info updated successfully",
        details: data.message,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/billing/info"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "update-billing-failed",
        message: "Failed to update billing info",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCheckSubscriptionHealth = ({
  enabled = true,
}: {
  enabled?: boolean;
}): UseQueryResult<CheckSubscriptionHealthResponseBody, ErrorNotification> => {
  const queryClient = useQueryClient();
  return useQuery<CheckSubscriptionHealthResponseBody, ErrorNotification>({
    queryKey: ["/api/v1/billing/check-subscription-health"],
    queryFn: async (): Promise<CheckSubscriptionHealthResponseBody> => {
      try {
        const res = await axios.get(
          "/api/v1/billing/check-subscription-health"
        );
        queryClient.invalidateQueries({
          queryKey: ["/api/auth/v1/status"],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/auth/v1/user"],
        });
        return res.data;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    retry: false,
    enabled,
  });
};

export const cancelOrKeepSubscription = async ({
  cancel,
}: SetCancelAtPeriodEndRequestBody): Promise<SetCancelAtPeriodEndResponseBody> => {
  try {
    const res = await axios.post("/api/v1/billing/set-cancel-at-period-end", {
      cancel,
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useCancelOrKeepSubscription = (): UseMutationResult<
  SetCancelAtPeriodEndResponseBody,
  ErrorNotification,
  SetCancelAtPeriodEndRequestBody
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation<
    SetCancelAtPeriodEndResponseBody,
    ErrorNotification,
    SetCancelAtPeriodEndRequestBody
  >({
    mutationFn: cancelOrKeepSubscription,
    onSuccess: (data: SetCancelAtPeriodEndResponseBody): void => {
      setNotification({
        id: "cancel-or-keep-subscription-success",
        message: data.cancelAtPeriodEnd
          ? "Subscription cancelled successfully"
          : "Subscription will continue",
        details: data.message,
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/billing/check-subscription-health"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "cancel-or-keep-subscription-failed",
        message: "Failed to cancel subscription",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchOrganizationInvoices = async (
  payload: GetInvoicesQuery
): Promise<GetInvoicesResponseBody> => {
  try {
    const res = await axios.get("/api/v1/billing/invoices", {
      params: payload,
    });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetOrganizationInvoices = (payload: GetInvoicesQuery) => {
  return useQuery({
    queryKey: [
      "/api/v1/billing/invoices",
      payload.startDate,
      payload.endDate,
      payload,
    ],
    queryFn: () => fetchOrganizationInvoices(payload),
    retry: false,
  });
};
